/**
 * Descout dashboard — pure, non-client formatters + lookup tables.
 *
 * NO 'use client' — this module is imported by RSC server pages for
 * server-side rendering. Keep it free of any browser/React APIs.
 */

/* ---------- price formatter ---------- */
export function fmtPrice(p: number | null | undefined): string {
  if (p == null) return '—';
  if (p >= 1)
    return (
      '$' +
      p.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    );
  if (p >= 0.01) return '$' + p.toFixed(4);
  if (p >= 0.0001) return '$' + p.toFixed(5);
  return '$' + p.toFixed(6);
}

/* ---------- compact USD formatter (KPI tiles / TVL) ---------- */
export function fmtCompactUsd(n: number): string {
  if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
  return `$${n.toFixed(0)}`;
}

/* ---------- NFT floor in its native currency (e.g. "0.42 ETH") ---------- */
export function fmtFloor(floor: number, symbol: string): string {
  if (!floor || floor <= 0) return '—';
  const value = floor >= 1 ? floor.toFixed(2) : floor.toFixed(3);
  return `${value} ${symbol}`;
}

/* ─────────────────────────────────────────────────────────────────────────────
   Card vocabulary — the THREE locked tiers (Tailwind class strings, usable from
   RSC pages directly on crawlable <Link>/<section> elements):

     1. CARD          — plain hairline border (the default surface tile)
     2. CARD_TOPRULE  — 2px gold top-rule (news / highlight cards)
     3. CARD_FEATURED — 3px gold left-spine (+ FEATURED_GLOW bg) — reserved for
                        THE one featured cell per surface.

   CARD_HOVER is the single hover treatment for card-as-link tiles.
   Keep in sync with the inline-style `Card` atom in ./atoms.tsx (client surfaces).
───────────────────────────────────────────────────────────────────────────── */
export const CARD = 'bg-surface border-[0.5px] border-divider rounded';
export const CARD_HOVER = 'transition-colors hover:border-scout-gold/40';
export const CARD_TOPRULE = `${CARD} border-t-2 border-t-scout-gold/70`;
export const CARD_FEATURED = `${CARD} border-l-[3px] border-l-scout-gold`;

/** Radial glow for the featured (tier-3) cell — pass as `style={{ background }}`. */
export const FEATURED_GLOW =
  'radial-gradient(ellipse 60% 80% at 90% 50%, rgba(232,168,50,0.06) 0%, transparent 70%)';
/** Radial glow under a page header hero. */
export const HEADER_GLOW =
  'radial-gradient(ellipse 70% 80% at 50% 110%, rgba(232,168,50,0.06) 0%, transparent 65%)';

/** ONE inter-section spacing token (26px — matches the validated prototype). */
export const SECTION_GAP = 'mb-[26px]';

/* ---------- entity type registry ---------- */
export interface EntityTypeEntry {
  label: string;
  glyph: string;
}

export type EntityTypeKey =
  | 'project'
  | 'game'
  | 'character'
  | 'person'
  | 'organization'
  | 'protocol'
  | 'token';

export const ENTITY_TYPES: Record<EntityTypeKey, EntityTypeEntry> = {
  project:      { label: 'Project',      glyph: 'P' },
  game:         { label: 'Game',         glyph: 'G' },
  character:    { label: 'Character',    glyph: 'C' },
  person:       { label: 'Person',       glyph: '@' },
  organization: { label: 'Organization', glyph: 'O' },
  protocol:     { label: 'Protocol',     glyph: '∆' },
  token:        { label: 'Token',        glyph: '$' },
};

/* ---------- initials helper ---------- */
export function initials(name: string | null | undefined): string {
  return (
    (name ?? '')
      .replace(/[^A-Za-z0-9 ]/g, '')
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((w) => w[0])
      .join('')
      .toUpperCase() || '··'
  );
}
